import { useEffect, useMemo, useRef, useState } from "react";
import { MarkerClusterer, SuperClusterAlgorithm } from "@googlemaps/markerclusterer";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../utils/axiosInstance";
import Select from "react-select";
import { Menu } from "lucide-react";
import { toast } from "react-toastify";
import LinkedContextDialog from "../components/LinkedContext/LinkedContextDialog";

const GOOGLE_MAP_JS_KEY =
    process.env.REACT_APP_GOOGLE_MAP_API_KEY || "AIzaSyCh7OYpAumSRyc0CpKUPRKmEX6c_JDKa_w";

const ALGO_MAX_ZOOM = 20;

const CATEGORY_ORDER = [
    "Systems House",
    "Fabricator",
    "Operable Walls",
    "School",
    "Washrooms",
    "Transport",
    "Builder",
    "Architect",
    "Competitor",
    "Reseller",
    "Other",
    // "Uncategorised",
];

const PRESET = {
    "Systems House": "#005AB5",
    Fabricator: "#FF6F00",
    "Operable Walls": "#7A1FA2",
    School: "#F0E442",
    Washrooms: "#00A08A",
    Transport: "#E0001A",
    Builder: "#7F3C1E",
    Architect: "#8A8A00",
    Competitor: "#FF2D95",
    Reseller: "#117733",
    Other: "#5C5C5C",
    Uncategorised: "#BDBDBD",
};

const PALETTE = [
    "#2c7fb8",
    "#7fcdbb",
    "#c7e9c0",
    "#9ecae1",
    "#fdd0a2",
    "#b2df8a",
    "#fccde5",
    "#a6d96a",
    "#ff9896",
    "#c49c94",
];

const CANONICAL = {
    "systems house": "Systems House",
    "system house": "Systems House",
    dealer: "Reseller",
    reseller: "Reseller",
    other: "Other",
    others: "Other",
    uncategorised: "Uncategorised",
    uncategorized: "Uncategorised",
};

function normalizeCategory(v) {
    const t = String(v || "").trim().toLowerCase();
    if (!t) return "Uncategorised";
    const hit = CATEGORY_ORDER.find((c) => c.toLowerCase() === t);
    if (hit) return hit;
    return CANONICAL[t] || t.replace(/\b\w/g, (m) => m.toUpperCase());
}

// Robust loader: memoized, ensures google.maps.Map exists.
let mapsLoaderPromise;
function loadGoogleMaps(key) {
    if (window.google?.maps?.Map) return Promise.resolve();
    if (mapsLoaderPromise) return mapsLoaderPromise;

    mapsLoaderPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-gmaps="true"]');
        if (!existing) {
            const s = document.createElement("script");
            s.src = `https://maps.googleapis.com/maps/api/js?key=${key}&v=weekly&language=en&region=AU&loading=async`;
            s.async = true;
            s.defer = true;
            s.dataset.gmaps = "true";
            s.onerror = reject;
            s.onload = () => {
                const start = Date.now();
                (function waitReady() {
                    if (window.google?.maps?.Map) return resolve();
                    if (window.google?.maps?.importLibrary) {
                        window.google.maps
                            .importLibrary("maps")
                            .then(() => resolve())
                            .catch(reject);
                        return;
                    }
                    if (Date.now() - start > 10000) return reject(new Error("Google Maps not ready"));
                    setTimeout(waitReady, 25);
                })();
            };
            document.head.appendChild(s);
        } else {
            const start = Date.now();
            (function waitReady() {
                if (window.google?.maps?.Map) return resolve();
                if (window.google?.maps?.importLibrary) {
                    window.google.maps
                        .importLibrary("maps")
                        .then(() => resolve())
                        .catch(reject);
                    return;
                }
                if (Date.now() - start > 10000) return reject(new Error("Google Maps not ready"));
                setTimeout(waitReady, 25);
            })();
        }
    });

    return mapsLoaderPromise;
}

function clusterSvg(color, count) {
    const size = Math.max(20, Math.min(42, 20 + Math.log(count + 1) * 8));
    const r = size / 2;

    const fontSize = Math.max(9, Math.round(size * 0.38));

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
    <defs>
      <filter id="s">
        <feDropShadow dx="0" dy="1.5" stdDeviation="1.5" flood-color="rgba(0,0,0,0.25)"/>
      </filter>

      <!-- Edge fades lighter -->
      <radialGradient id="g" cx="50%" cy="45%" r="75%">
        <stop offset="0%" stop-color="${color}" stop-opacity="1"/>
        <stop offset="40%" stop-color="${color}" stop-opacity="0.8"/>
        <stop offset="80%" stop-color="${color}" stop-opacity="0.4"/>
        <stop offset="100%" stop-color="${color}" stop-opacity="0.10"/>
      </radialGradient>
    </defs>

    <circle cx="${r}" cy="${r}" r="${r - 1}" fill="url(#g)" filter="url(#s)"/>

    <text x="${r}" y="${r}"
          text-anchor="middle"
          dominant-baseline="middle"
          font-size="${fontSize}"
          font-family="system-ui, -apple-system, Segoe UI, Roboto, Arial"
          font-weight="400"
          fill="white">
      ${count}
    </text>
  </svg>`;

    return {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        scaledSize: new window.google.maps.Size(size, size),
        anchor: new window.google.maps.Point(r, r),
    };
}

function svgIcon(color, zoom) {
    const z = Math.max(2, Math.min(zoom || 4, 20));
    const size = Math.round(8 + z * 1.8);
    const r = size / 2;
    const stroke = Math.max(2, Math.round(size * 0.18));
    const inner = Math.max(2, Math.round(r - stroke - 1));
    const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'>
    <defs><filter id='s'><feDropShadow dx='0' dy='1' stdDeviation='1' flood-color='rgba(0,0,0,0.35)'/></filter></defs>
    <circle cx='${r}' cy='${r}' r='${r - 1}' fill='${color}' stroke='white' stroke-width='${stroke}' filter='url(#s)'/>
    <circle cx='${r}' cy='${r}' r='${inner}' fill='white' fill-opacity='0.18'/>
  </svg>`;
    return {
        url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
        anchor: new window.google.maps.Point(r, r),
        scaledSize: new window.google.maps.Size(size, size),
    };
}

// 44px react-select styling (matches your Dashboard style)
const selectStyles44 = {
    control: (p) => ({
        ...p,
        minHeight: 44,
        height: 44,
        borderColor: "#e2e8f0",
        boxShadow: "none",
        borderRadius: 12,
        "&:hover": { borderColor: "#cbd5e1" },
    }),
    valueContainer: (p) => ({ ...p, height: 44, padding: "0 12px" }),
    input: (p) => ({ ...p, margin: 0, padding: 0 }),
    indicatorsContainer: (p) => ({ ...p, height: 44 }),
    option: (p, s) => ({
        ...p,
        backgroundColor: s.isSelected ? "#0f172a" : s.isFocused ? "#f1f5f9" : undefined,
        color: s.isSelected ? "white" : "#0f172a",
    }),
    menu: (p) => ({ ...p, borderRadius: 12, overflow: "hidden" }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const DATE_OPTIONS = [
    { value: "1m", label: "Last 1 month" },
    { value: "3m", label: "Last 3 months" },
    { value: "6m", label: "Last 6 months" },
    { value: "12m", label: "Last 12 months" },
    { value: "all", label: "All" },
];

const DYNAMICS_OPTIONS = [
    { value: "all", label: "All" },
    { value: "assigned_in", label: "Dynamics Salesperson Assigned" },
    { value: "everything_else", label: "Everything Else" },
];

export default function MapView() {
    const mapRef = useRef(null);
    const map = useRef(null);
    const markers = useRef([]);
    const clusterer = useRef(null);
    const algo = useRef(null);
    const hasInitializedCategories = useRef(false);

    const [cats, setCats] = useState([]);
    const [active, setActive] = useState(new Set());
    const [colors, setColors] = useState({});
    const [showFilters, setShowFilters] = useState(false);

    // Details dialog state
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState("");
    const [details, setDetails] = useState(null);
    const detailsAbortRef = useRef(null);
    const [isLinkedDialogOpen, setIsLinkedDialogOpen] = useState(false);
    const [linkedLoading, setLinkedLoading] = useState(false);
    const [linkedRecord, setLinkedRecord] = useState(null);
    const [linkedContacts, setLinkedContacts] = useState([]);
    const [blacklistBusy, setBlacklistBusy] = useState(false);

    const [minRelevance, setMinRelevance] = useState(0);
    const [dateRange, setDateRange] = useState("all"); // "3m" | "12m" | "all"
    const [dynamicsPresence, setDynamicsPresence] = useState("assigned_in");
    const [totalCount, setTotalCount] = useState(null);
    const [filteredPoints, setFilteredPoints] = useState([]);

    const visibleByCategoryCount = useMemo(() => {
        return filteredPoints.filter((r) => active.has(normalizeCategory(r.category))).length;
    }, [filteredPoints, active]);

    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();
        (async () => {
            try {
                const params = new URLSearchParams();
                params.set("min_relevance", "0");
                const { data } = await axiosInstance.get(`/record/records/map-points-lite?${params.toString()}`, {
                    signal: controller.signal,
                });
                if (!mounted) return;
                setTotalCount(Array.isArray(data) ? data.length : 0);
            } catch (err) {
                if (err?.name === "CanceledError" || err?.name === "AbortError") return;
                console.error("map total-count fetch failed", err);
            }
        })();
        return () => {
            mounted = false;
            controller.abort();
        };
    }, []);

    const openDetailsById = async (recordId) => {
        setDetailsOpen(true);
        setDetailsError("");
        setDetailsLoading(true);
        try {
            if (detailsAbortRef.current) detailsAbortRef.current.abort();
            detailsAbortRef.current = new AbortController();
            const { data } = await axiosInstance.get(`/record/records/${recordId}`, {
                signal: detailsAbortRef.current.signal,
            });
            setDetails(data || null);
        } catch (err) {
            if (err.name !== "CanceledError" && err.name !== "AbortError") {
                setDetailsError(err.response?.data?.error || "Failed to load record");
            }
        } finally {
            setDetailsLoading(false);
        }
    };

    const openLinkedContactsDialog = async (record) => {
        if (!record?.record_id) return;
        setIsLinkedDialogOpen(true);
        setLinkedLoading(true);
        setLinkedRecord(record);
        setLinkedContacts([]);
        try {
            const resp = await axiosInstance.get(`/record/records/${record.record_id}/contacts`);
            setLinkedContacts(Array.isArray(resp?.data) ? resp.data : []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load linked contacts.");
        } finally {
            setLinkedLoading(false);
        }
    };

    const addDomainToBlacklist = async (domain) => {
        const d = (domain || "").trim();
        if (!d) return;
        setBlacklistBusy(true);
        try {
            await axiosInstance.post("/blacklist/blacklists", { domain: d });
            toast.success(`Added "${d}" to blacklist.`);
        } catch (err) {
            toast.error(err.response?.data?.error || "Failed to add blacklist domain.");
        } finally {
            setBlacklistBusy(false);
        }
    };

    useEffect(() => {
        let mounted = true;
        const controller = new AbortController();

        (async () => {
            await loadGoogleMaps(GOOGLE_MAP_JS_KEY);
            if (!mounted || !mapRef.current) return;

            // Map instance (create once)
            if (!map.current) {
                map.current = new window.google.maps.Map(mapRef.current, {
                    mapTypeControl: false,
                    maxZoom: ALGO_MAX_ZOOM,
                    gestureHandling: "greedy",
                    tilt: 0,
                });

                const WORLD_NO_POLES = new window.google.maps.LatLngBounds(
                    new window.google.maps.LatLng(-60, -180),
                    new window.google.maps.LatLng(70, 180)
                );

                map.current.fitBounds(WORLD_NO_POLES);

                let fitted = false;
                map.current.addListener("idle", () => {
                    if (fitted) return;
                    fitted = true;
                    map.current.setOptions({
                        minZoom: map.current.getZoom(),
                        restriction: { latLngBounds: WORLD_NO_POLES, strictBounds: true },
                    });
                });

                map.current.addListener("zoom_changed", () => {
                    const z = map.current.getZoom();
                    markers.current.forEach((m) => m.setIcon(svgIcon(m._color, z)));
                });
            }

            // Fetch lite points with filters
            const params = new URLSearchParams();
            params.set("min_relevance", String(Math.max(0, Number(minRelevance) || 0)));
            if (String(dateRange)) params.set("date_range", String(dateRange));
            if (dynamicsPresence && dynamicsPresence !== "all") {
                params.set("dynamics_bucket", dynamicsPresence);
            }

            const url = `/record/records/map-points-lite${params.toString() ? `?${params.toString()}` : ""
                }`;

            const { data: lite } = await axiosInstance.get(url, { signal: controller.signal });
            if (!mounted) return;
            setFilteredPoints(Array.isArray(lite) ? lite : []);

            const present = [...new Set(lite.map((r) => normalizeCategory(r.category)))];
            const extras = present.filter((c) => !CATEGORY_ORDER.includes(c));
            const sortedCats = [...CATEGORY_ORDER, ...extras];

            setCats(sortedCats);
            setActive((prev) => {
                if (!hasInitializedCategories.current) {
                    hasInitializedCategories.current = true;
                    return new Set(sortedCats);
                }
                const next = new Set([...prev].filter((c) => sortedCats.includes(c)));
                return next;
            });

            const COLORS = { ...PRESET };
            let i = 0;
            for (const c of sortedCats) {
                if (!COLORS[c]) COLORS[c] = PALETTE[i++ % PALETTE.length];
            }
            setColors(COLORS);

            // Clear existing markers + clusterer markers
            if (clusterer.current) clusterer.current.clearMarkers();
            markers.current.forEach((m) => m.setMap(null));
            markers.current = [];

            // Create markers
            markers.current = lite.map((r) => {
                const cat = normalizeCategory(r.category);
                const m = new window.google.maps.Marker({
                    position: { lat: r.lat, lng: r.lng },
                    title: cat,
                    icon: svgIcon(COLORS[cat], map.current.getZoom()),
                    optimized: false,
                    zIndex: 100,
                });
                m._rid = r.record_id;
                m._cat = cat;
                m._color = COLORS[cat];
                m.addListener("click", () => openDetailsById(m._rid));
                return m;
            });

            // Clusterer
            if (!algo.current) algo.current = new SuperClusterAlgorithm({ maxZoom: 16, radius: 70 });

            if (!clusterer.current) {
                clusterer.current = new MarkerClusterer({
                    map: map.current,
                    markers: markers.current,
                    algorithm: algo.current,
                    renderer: {
                        // cluster colour
                        render({ count, position }) {
                            const color = "#111729";
                            return new window.google.maps.Marker({
                                position,
                                icon: clusterSvg(color, count),
                                zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
                            });
                        },
                    },
                    onClusterClick: function (_event, cluster, theMap) {
                        const b = new window.google.maps.LatLngBounds();
                        for (const mk of cluster.markers) b.extend(mk.getPosition());
                        theMap.fitBounds(b, 80);
                    },
                });
            } else {
                clusterer.current.addMarkers(markers.current);
            }
        })().catch((err) => {
            if (err?.name === "CanceledError" || err?.name === "AbortError") return;
            console.error(err);
            toast.error(err?.response?.data?.error || "Failed to load map points.");
        });

        return () => {
            mounted = false;
            controller.abort();
            if (clusterer.current) clusterer.current.clearMarkers();
            markers.current.forEach((m) => m.setMap(null));
            markers.current = [];
        };
    }, [minRelevance, dateRange, dynamicsPresence]);

    useEffect(() => {
        if (!clusterer.current) return;
        const keep = markers.current.filter((m) => active.has(m._cat));
        clusterer.current.clearMarkers();
        clusterer.current.addMarkers(keep);
    }, [active]);

    function toggle(cat) {
        const next = new Set(active);
        if (next.has(cat)) next.delete(cat);
        else next.add(cat);
        setActive(next);
    }

    const dateValue = DATE_OPTIONS.find((o) => o.value === dateRange) || DATE_OPTIONS[DATE_OPTIONS.length - 1];

    return (
        <div className="w-full max-w-full px-3 sm:px-4 md:px-6 py-4 bg-white rounded-lg shadow-md min-h-[100svh] max-h-[100lvh] flex flex-col overflow-hidden">
            {/* Top bar */}
            <div className="shrink-0 sticky top-0 z-20 bg-white max-w-full">
                <h1 className="text-xl xs:text-2xl sm:text-3xl md:text-4xl font-extrabold mb-2 sm:mb-3 md:mb-4 text-gray-800 border-b pb-2 sm:pb-3 md:pb-4 text-center">
                    Map View
                </h1>
            </div>

            {/* Mobile: filter controls row (prevents hamburger covering date filter) */}
            <div className="hidden responsive-trigger:flex items-start justify-between gap-3 mb-4">
                <div className="flex-1 w-full border border-slate-200 bg-white rounded-2xl shadow-sm p-3">
                    <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                    Relevance minimum
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className="h-11 px-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-700 text-sm font-semibold inline-flex items-center">
                                        {">"}=
                                    </span>
                                    <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={minRelevance}
                                        onChange={(e) => setMinRelevance(Number(e.target.value || 0))}
                                        className="w-24 h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                                    />
                                    <span className="text-xs text-slate-500">0-100</span>
                                </div>
                            </div>

                            <div>
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                    Date added
                                </div>
                                <Select
                                    value={dateValue}
                                    onChange={(v) => setDateRange((v && v.value) || "all")}
                                    options={DATE_OPTIONS}
                                    styles={selectStyles44}
                                    menuPortalTarget={document.body}
                                    isSearchable={false}
                                    classNamePrefix="react-select"
                                />
                            </div>
                        </div>

                        <div className="sm:max-w-[280px]">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                Dynamics Filter
                            </div>
                            <Select
                                value={DYNAMICS_OPTIONS.find((o) => o.value === dynamicsPresence)}
                                onChange={(v) => setDynamicsPresence((v && v.value) || "assigned_in")}
                                options={DYNAMICS_OPTIONS}
                                styles={selectStyles44}
                                menuPortalTarget={document.body}
                                isSearchable={false}
                                classNamePrefix="react-select"
                            />
                        </div>

                        <div className="text-sm text-slate-600">
                            {totalCount === null ? (
                                <>
                                    Count: <span className="font-semibold">{visibleByCategoryCount}</span>
                                    {" "}records
                                </>
                            ) : (
                                <>
                                    Count: <span className="font-semibold">{visibleByCategoryCount}</span>
                                    {" / "}
                                    {totalCount} records
                                </>
                            )}
                        </div>
                    </div>
                </div>

                <button
                    type="button"
                    onClick={() => setShowFilters(true)}
                    className="shrink-0 h-11 px-4 rounded-2xl border border-slate-200 bg-white text-slate-800 shadow-sm inline-flex items-center gap-2 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/20"
                    aria-label="Open filters"
                >
                    <Menu className="h-5 w-5" />
                    Filters
                </button>
            </div>

            {/* Hamburger button for constrained viewports (kept for compatibility; now redundant because we show the row above) */}
            {/* <button
                type="button"
                onClick={() => setShowFilters(true)}
                className="hidden responsive-trigger:flex items-center gap-2 fixed right-4 sm:right-6 top-[85px] sm:top-[105px] z-30 h-10 px-3 rounded-xl border bg-white text-gray-800 border-gray-300 shadow"
                aria-label="Open filters"
            >
                <Menu className="h-5 w-5" />
                Filters
            </button> */}

            {/* Bottom sheet (top/down, not left/right) for filters */}
            {showFilters && (
                <div className="fixed inset-0 z-40">
                    <div className="absolute inset-0 bg-black/30" onClick={() => setShowFilters(false)} />
                    <div
                        className="absolute left-0 right-0 bottom-0 bg-white shadow-2xl border-t border-gray-200 rounded-t-2xl p-4 max-h-[85vh] overflow-y-auto"
                        role="dialog"
                        aria-modal="true"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div>
                                <div className="mx-auto h-1.5 w-12 rounded-full bg-gray-200 mb-3" />
                                <h2 className="text-lg font-semibold">Filters</h2>
                            </div>
                            <button
                                onClick={() => setShowFilters(false)}
                                className="h-9 px-3 rounded-lg border bg-white hover:bg-gray-50"
                                aria-label="Close filters"
                            >
                                Close
                            </button>
                        </div>

                        <CategoryFilter
                            cats={cats}
                            active={active}
                            setActive={setActive}
                            toggle={toggle}
                            colors={colors}
                            orientation="col"
                        />
                    </div>
                </div>
            )}

            {/* Inline filters */}
            <div className="responsive-inline:block w-full max-w-full">
                <CategoryFilter
                    cats={cats}
                    active={active}
                    setActive={setActive}
                    toggle={toggle}
                    colors={colors}
                    orientation="row"
                />
            </div>

                        {/* Desktop relevance/date controls (beautified, uses 44px + react-select) */}
            <div className="responsive-inline:block w-full max-w-full">
                <div className="w-full max-w-full p-3 sm:p-4 mb-4 border border-slate-200 rounded-2xl bg-slate-50">
                    <div className="flex flex-wrap items-end gap-4">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                Relevance minimum
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold inline-flex items-center shadow-sm">
                                    ≥
                                </span>
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={minRelevance}
                                    onChange={(e) => setMinRelevance(Number(e.target.value || 0))}
                                    className="w-24 h-11 px-3 rounded-xl border border-slate-200 bg-white text-slate-800 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                                />
                                <span className="text-xs text-slate-500">0-100</span>
                            </div>
                        </div>

                        <div className="min-w-[220px] w-full sm:w-72">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                Date added
                            </div>
                            <Select
                                value={dateValue}
                                onChange={(v) => setDateRange((v && v.value) || "all")}
                                options={DATE_OPTIONS}
                                styles={selectStyles44}
                                menuPortalTarget={document.body}
                                isSearchable={false}
                                classNamePrefix="react-select"
                            />
                        </div>

                        <div className="min-w-[280px] w-full sm:w-[320px]">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                                Dynamics Filter
                            </div>
                            <Select
                                value={DYNAMICS_OPTIONS.find((o) => o.value === dynamicsPresence)}
                                onChange={(v) => setDynamicsPresence((v && v.value) || "assigned_in")}
                                options={DYNAMICS_OPTIONS}
                                styles={selectStyles44}
                                menuPortalTarget={document.body}
                                isSearchable={false}
                                classNamePrefix="react-select"
                            />
                        </div>

                    </div>

                    <div className="mt-3 text-sm text-slate-600">
                        {totalCount === null ? (
                            <>
                                Count: <span className="font-semibold">{visibleByCategoryCount}</span>
                                {" "}records
                            </>
                        ) : (
                            <>
                                Count: <span className="font-semibold">{visibleByCategoryCount}</span>
                                {" / "}
                                {totalCount} records
                            </>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
        #mapwrap .gm-ui-hover-effect{display:none !important;}

        .filter-scroll {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: thin;
        }
        .filter-scroll::-webkit-scrollbar { height: 6px; }
        .filter-scroll::-webkit-scrollbar-thumb {
          background: rgba(0,0,0,.15);
          border-radius: 9999px;
        }

        /* Constrained viewports: hide inline filters, show hamburger (bottom sheet used) */
        @media (orientation: landscape) and (max-height: 900px) {
          .responsive-inline\\:block { display: none !important; }
          .responsive-trigger\\:flex { display: flex !important; }
        }
        @media (max-width: 640px) {
          .responsive-inline\\:block { display: none !important; }
          .responsive-trigger\\:flex { display: flex !important; }
        }

        /* Roomy screens: show inline filters, hide hamburger */
        @media (min-width: 641px) and (orientation: portrait), (min-height: 901px) {
          .responsive-inline\\:block { display: block !important; }
          .responsive-trigger\\:flex { display: none !important; }
        }
      `}</style>

            {/* Map (UNCHANGED) */}
            <div id="mapwrap" ref={mapRef} className="flex-1 min-h-0 rounded-md w-full max-w-full" />

            {/* Details Dialog */}
            <DetailsDialog
                open={detailsOpen}
                onClose={() => setDetailsOpen(false)}
                loading={detailsLoading}
                error={detailsError}
                data={details}
                onOpenLinkedContext={openLinkedContactsDialog}
                onAddBlacklist={addDomainToBlacklist}
                blacklistBusy={blacklistBusy}
            />

            <LinkedContextDialog
                isOpen={isLinkedDialogOpen}
                onClose={() => setIsLinkedDialogOpen(false)}
                title="Linked Contacts"
                loading={linkedLoading}
                record={linkedRecord}
                contacts={linkedContacts}
                onAddBlacklist={addDomainToBlacklist}
                blacklistBusy={blacklistBusy}
            />
        </div>
    );
}

function CategoryFilter({ cats = [], active, toggle, setActive, colors = {}, orientation = "row" }) {
    const allOn = active?.size === cats.length;
    const noneOn = active?.size === 0;
    const isRow = orientation === "row";

    return (
        <div className="p-2 xs:p-3 sm:p-4 shrink-0 max-w-full">
            {isRow ? (
                <div className="flex items-center gap-2 sm:gap-3 max-w-full">
                    <button
                        type="button"
                        onClick={() => setActive(new Set(cats))}
                        className={`h-9 px-3 rounded-xl border text-sm font-medium shadow-sm transition shrink-0
              ${allOn
                                ? "bg-slate-800 text-white border-slate-800"
                                : "bg-white text-slate-800 border-slate-300 hover:bg-slate-50"
                            }`}
                    >
                        All
                    </button>

                    <button
                        type="button"
                        onClick={() => setActive(new Set())}
                        className={`h-9 px-3 rounded-xl border text-sm font-medium shadow-sm transition shrink-0
              ${noneOn
                                ? "bg-slate-800 text-white border-slate-800"
                                : "bg-white text-slate-800 border-slate-300 hover:bg-slate-50"
                            }`}
                    >
                        None
                    </button>

                    <span className="h-6 w-px bg-gray-200 mx-1 sm:mx-2 shrink-0" aria-hidden="true" />

                    <div className="filter-scroll flex-1 min-w-0 max-w-full">
                        <div className="flex flex-nowrap items-center gap-2 sm:gap-3">
                            {cats.map((cat) => {
                                const checked = active.has(cat);
                                const dot = colors[cat] || "#999";
                                return (
                                    <label
                                        key={cat}
                                        className={`relative group inline-flex items-center gap-2 h-9 pl-9 pr-3 rounded-xl border text-xs sm:text-sm cursor-pointer select-none transition whitespace-nowrap
                      ${checked
                                                ? "border-slate-800 bg-slate-50 text-slate-800"
                                                : "border-slate-300 bg-white text-slate-800 hover:bg-slate-50"
                                            }`}
                                    >
                                        <span
                                            className={`absolute left-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-lg border transition
                        ${checked ? "border-slate-800 bg-slate-800" : "border-slate-300 bg-white"
                                                }`}
                                        >
                                            <AnimatePresence initial={false}>
                                                {checked && (
                                                    <motion.svg
                                                        viewBox="0 0 26 26"
                                                        className="h-5 w-5 text-white m-[2px]"
                                                        initial={{ scale: 0.6, opacity: 0 }}
                                                        animate={{ scale: 1, opacity: 1 }}
                                                        exit={{ scale: 0.6, opacity: 0 }}
                                                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                                    >
                                                        <path
                                                            fill="currentColor"
                                                            d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"
                                                        />
                                                    </motion.svg>
                                                )}
                                            </AnimatePresence>
                                        </span>

                                        <input type="checkbox" checked={checked} onChange={() => toggle(cat)} className="sr-only" />

                                        <span
                                            className="inline-block w-3.5 h-3.5 rounded-full ring-1 ring-black/10"
                                            style={{ background: dot }}
                                            aria-hidden="true"
                                        />
                                        <span className="font-medium">{cat}</span>
                                    </label>
                                );
                            })}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="max-w-full">
                    <div className="mb-3 flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setActive(new Set(cats))}
                            className={`h-9 px-3 rounded-xl border text-sm font-medium shadow-sm transition
                ${allOn
                                    ? "bg-slate-600 text-white border-slate-600"
                                    : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
                                }`}
                        >
                            All
                        </button>
                        <button
                            type="button"
                            onClick={() => setActive(new Set())}
                            className={`h-9 px-3 rounded-xl border text-sm font-medium shadow-sm transition
                ${noneOn
                                    ? "bg-slate-600 text-white border-slate-600"
                                    : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
                                }`}
                        >
                            None
                        </button>
                    </div>

                    <div className="max-h-[calc(85vh-10rem)] overflow-y-auto pr-1">
                        <ul className="flex flex-col gap-2">
                            {cats.map((cat) => {
                                const checked = active.has(cat);
                                const dot = colors[cat] || "#999";
                                return (
                                    <li key={cat}>
                                        <label
                                            className={`relative group flex items-center gap-3 rounded-lg border p-3 text-sm cursor-pointer select-none transition
                        ${checked
                                                    ? "border-slate-600 bg-slate-50 text-slate-800"
                                                    : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                                                }`}
                                        >
                                            <span
                                                className={`h-6 w-6 rounded-lg border flex items-center justify-center
                          ${checked ? "border-slate-600 bg-slate-600" : "border-gray-300 bg-white"
                                                    }`}
                                            >
                                                <AnimatePresence initial={false}>
                                                    {checked && (
                                                        <motion.svg
                                                            viewBox="0 0 26 26"
                                                            className="h-5 w-5 text-white"
                                                            initial={{ scale: 0.6, opacity: 0 }}
                                                            animate={{ scale: 1, opacity: 1 }}
                                                            exit={{ scale: 0.6, opacity: 0 }}
                                                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                                        >
                                                            <path
                                                                fill="currentColor"
                                                                d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z"
                                                            />
                                                        </motion.svg>
                                                    )}
                                                </AnimatePresence>
                                            </span>

                                            <input type="checkbox" checked={checked} onChange={() => toggle(cat)} className="sr-only" />

                                            <span
                                                className="inline-block w-3.5 h-3.5 rounded-full ring-1 ring-black/10"
                                                style={{ background: dot }}
                                                aria-hidden="true"
                                            />
                                            <span className="font-medium">{cat}</span>
                                        </label>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

function normalizeHttpUrl(u = "") {
    const s = String(u).trim();
    if (!s) return "";
    return /^https?:\/\//i.test(s) ? s : `http://${s}`;
}

function DetailsRow({ label, children }) {
    return (
        <div className="mb-3">
            <div className="text-xs font-bold text-gray-800 mb-1">{label}</div>
            <div className="text-sm text-gray-900 break-words">{children}</div>
        </div>
    );
}

function DetailsDialog({ open, onClose, loading, error, data, onOpenLinkedContext, onAddBlacklist, blacklistBusy = false }) {
    if (!open) return null;

    const d = data || {};
    const firstEmail = d.emails;
    const firstPhone = d.phone;
    const address = [d.street_address, d.city, d.state_province, d.zip_postal_code, d.country]
        .filter(Boolean)
        .join(", ");

    const Field = ({ label, children }) => (
        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
            <div className="mt-1 text-sm text-slate-900 break-words">{children}</div>
        </div>
    );

    return (
        <div className="fixed inset-0 z-[100]">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            <div
                role="dialog"
                aria-modal="true"
                className="absolute left-1/2 top-1/2 w-[min(760px,94vw)] max-h-[85vh] -translate-x-1/2 -translate-y-1/2
          bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                        <div className="text-lg font-extrabold text-slate-900">Company Details</div>
                        <div className="text-xs text-slate-500 mt-0.5">Record information</div>
                    </div>

                    <div className="flex items-center gap-2">
                        {!loading && !error && d?.record_id ? (
                            <button
                                onClick={() => onOpenLinkedContext(d)}
                                className="h-9 px-3 rounded-xl border border-slate-200 bg-slate-900 text-white hover:bg-slate-800 shadow-sm"
                                type="button"
                            >
                                Linked Contacts
                            </button>
                        ) : null}
                        {!loading && !error && d?.domain ? (
                            <button
                                onClick={() => onAddBlacklist(d.domain)}
                                disabled={blacklistBusy}
                                className="h-9 px-3 rounded-xl border border-rose-200 bg-white text-rose-700 hover:bg-rose-50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                type="button"
                            >
                                Blacklist
                            </button>
                        ) : null}
                        <button
                            onClick={onClose}
                            className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-sm"
                            aria-label="Close"
                        >
                            Close
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="p-5 overflow-auto bg-slate-50">
                    {loading ? (
                        <div className="py-16 flex items-center justify-center">
                            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm text-slate-700">
                                Loading...
                            </div>
                        </div>
                    ) : error ? (
                        <div className="py-16 flex items-center justify-center">
                            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 shadow-sm text-rose-700">
                                {error}
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Company">{d.company_name || "-"}</Field>

                            <Field label="Category">{d.business_category || "-"}</Field>

                            <Field label="Domain">
                                {d.domain ? (
                                    <a
                                        href={normalizeHttpUrl(d.domain)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-500"
                                    >
                                        {d.domain}
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </Field>

                            <Field label="LinkedIn">
                                {d.linkedin ? (
                                    <a
                                        href={normalizeHttpUrl(d.linkedin)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-500"
                                    >
                                        {d.linkedin}
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </Field>

                            <Field label="Email">
                                {firstEmail ? (
                                    <a
                                        href={`mailto:${firstEmail}`}
                                        className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-500"
                                    >
                                        {firstEmail}
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </Field>

                            <Field label="Phone">
                                {firstPhone ? (
                                    <a
                                        href={`tel:${firstPhone}`}
                                        className="text-slate-900 underline decoration-slate-300 underline-offset-2 hover:decoration-slate-500"
                                    >
                                        {firstPhone}
                                    </a>
                                ) : (
                                    "-"
                                )}
                            </Field>

                            <div className="sm:col-span-2">
                                <Field label="Address">{address || "-"}</Field>
                            </div>

                            <div className="sm:col-span-2">
                                <Field label="Summary">{d.summary || "-"}</Field>
                            </div>

                            <div className="sm:col-span-2">
                                <Field label="Keywords">{d.keywords || "-"}</Field>
                            </div>

                            <Field label="Relevance">{d.relevance ?? "-"}</Field>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

